import { useLang } from "@/lib/i18n";
import useNow from "@/hooks/useNow";
import { parseWeek, openStatus, formatTime, puertoRicoClock } from "@/lib/hours";

// Inside the last hour the useful thing to say is when the door shuts; before
// that the place is simply open.
const CLOSING_SOON = 60;

const DOT = {
  open: "bg-[#15803D]",
  soon: "bg-[#B45309]",
  closed: "bg-[#ef3340]",
};

/**
 * Whether a place is open at this minute, as one muted line of card metadata:
 * "Open · Closes 9pm", "Closing soon · 9pm", or "Closed · Opens 11am" — the
 * hour being today's, tomorrow's or the next day the week actually opens. It
 * sits in the card's text block so the photograph above it stays clear, reads
 * against the current time, and refreshes as the minutes pass. A place with no
 * weekly schedule gets a neutral TBD placeholder rather than a guessed status.
 */
export default function OpenStatusLine({ listing, className = "", compact = false }) {
  const { lang, t } = useLang();
  const now = useNow();
  const status = openStatus(parseWeek(listing?.hours_json), lang, puertoRicoClock(now));
  if (!status) {
    const writtenHours = String(listing?.hours_text || "").trim();
    return (
      <span
        className={`inline-flex min-w-0 items-center gap-1.5 ${compact ? "text-[11px] font-medium leading-4 tracking-[-0.01em] text-[#77736c] dark:text-[#b7b1a7]" : "text-[0.65rem] leading-[15px] tracking-[0.04em] font-bold text-[#77736c]"} ${className}`}
        title={writtenHours || undefined}
      >
        <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-[#b8b5ad]" aria-hidden="true" />
        <span className="min-w-0 truncate">
          {writtenHours
            ? `${lang === "es" ? "Horario" : "Hours"} · ${writtenHours}`
            : lang === "es" ? "Horario por confirmar" : "Hours TBD"}
        </span>
      </span>
    );
  }

  const state = !status.open ? "closed" : status.minutesLeft <= CLOSING_SOON ? "soon" : "open";

  const label =
    state === "closed" ? t("card.closed") : state === "soon" ? t("card.closingSoon") : t("card.open");

  // An open place states when it shuts; a shut one states when it opens again,
  // which day by day may be today, tomorrow, or later in the week.
  const detail =
    state === "closed"
      ? status.opensAt
        ? status.opensInDays === 1
          ? t("card.opensTomorrow", { time: formatTime(status.opensAt) })
          : status.opensDay
            ? t("card.opensOn", { day: status.opensDay, time: formatTime(status.opensAt) })
            : t("card.opensAt", { time: formatTime(status.opensAt) })
        : ""
      : state === "soon"
        // The word already says the place is closing, so the hour stands alone.
        ? formatTime(status.closesAt)
        : status.closesAt
          ? t("card.closesAt", { time: formatTime(status.closesAt) })
          : "";

  if (compact) {
    const compactLabel = status.open && status.closesAt
      ? `${lang === "es" ? "Cierra a las" : "Closes at"} ${formatTime(status.closesAt)}`
      : detail ? `${label} · ${detail}` : label;
    return (
      <span className={`inline-flex min-w-0 items-start gap-1.5 text-[11px] font-medium leading-4 tracking-[-0.01em] text-[#66645f] dark:text-[#c4bfb5] sm:text-[11.5px] ${className}`}>
        <span className={`mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full ${DOT[state]}`} aria-hidden="true" />
        <span className="min-w-0 break-words" title={compactLabel}>{compactLabel}</span>
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 min-w-0 text-[0.67rem] leading-[15px] tracking-[0.11em] uppercase font-black text-[#8b877f] ${className}`}
    >
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT[state]}`} />
      {/* One line while the card is wide enough for it, and never wider than
          the card: on a phone it wraps rather than running past the edge. */}
      <span className="min-w-0 break-words">{detail ? `${label} · ${detail}` : label}</span>
    </span>
  );
}